import PratusTextArea from 'components/DesignSystems/New/PratusTextArea';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import AssignedResourcesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/AssignedResourcesTable';
import CommandPrioritiesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CommandPrioritiesTable';
import CriticalResourceRequestsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/CriticalResourceRequestsTable';
import IncidentTypeCheckboxGroup from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/IncidentTypeCheckboxGroup';
import NotableItemsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/NotableItemsTable';
import PlannedActionsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/PlannedActionsTable';
import ReportVersionToggle from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ReportVersionToggle';
import ResponderDeathsTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderDeathsTable';
import ResponderInjuriesTable from 'components/IAP/planningView/widgets/IncidentStatusSummary/IncidentStatusSummaryComplexComponents/ResponderInjuriesTable';
import { Row } from 'react-bootstrap';
import { IncidentStatusSummaryFormFields } from 'types/iapBaseTypes';

export const SituationSummaryField = ({
  statusSummaryId,
  originalSituationSummary,
}: {
  statusSummaryId?: string;
  originalSituationSummary?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Situation Summary"
        onSave={(value) =>
          upsertIncidentStatusSummary({ situationSummary: value })
        }
        defaultValue={originalSituationSummary}
      />
    </Row>
  );
};

export const LifeSafetyHealthIssuesField = ({
  statusSummaryId,
  originalLifeSafetyHealthIssues,
}: {
  statusSummaryId?: string;
  originalLifeSafetyHealthIssues?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Life, Safety, and Health Issues"
        onSave={(value) =>
          upsertIncidentStatusSummary({ lifeSafetyHealthIssues: value })
        }
        defaultValue={originalLifeSafetyHealthIssues}
      />
    </Row>
  );
};

export const MarineTransportationSystemField = ({
  statusSummaryId,
  originalMarineTransportationSystem,
}: {
  statusSummaryId?: string;
  originalMarineTransportationSystem?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Impacts to Marine Transportation System and Critical Infrastructure"
        onSave={(value) =>
          upsertIncidentStatusSummary({ marineTransportationSystem: value })
        }
        defaultValue={originalMarineTransportationSystem}
        maxLength={1000}
      />
    </Row>
  );
};

export const AmplifyingInformationField = ({
  statusSummaryId,
  originalAmplifyingInformation,
}: {
  statusSummaryId?: string;
  originalAmplifyingInformation?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Amplifying Information"
        onSave={(value) =>
          upsertIncidentStatusSummary({ amplifyingInformation: value })
        }
        defaultValue={originalAmplifyingInformation}
        maxLength={1000}
      />
    </Row>
  );
};

export const CommentsField = ({
  statusSummaryId,
  originalComments,
}: {
  statusSummaryId?: string;
  originalComments?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Comments"
        onSave={(value) => upsertIncidentStatusSummary({ comments: value })}
        defaultValue={originalComments}
        maxLength={1000}
      />
    </Row>
  );
};

export const MediaPhotosPublicOutreachField = ({
  statusSummaryId,
  originalMediaPhotosPublicOutreach,
}: {
  statusSummaryId?: string;
  originalMediaPhotosPublicOutreach?: string;
}) => {
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  return (
    <Row>
      <PratusTextArea
        label="Media, Photos, and Public Outreach"
        onSave={(value) =>
          upsertIncidentStatusSummary({ mediaPhotosPublicOutreach: value })
        }
        defaultValue={originalMediaPhotosPublicOutreach}
        maxLength={1000}
      />
    </Row>
  );
};

const IncidentStatusSummaryFormComponents = ({
  statusSummaryId,
  originalIncidentStatusSummary,
}: {
  statusSummaryId: string;
  originalIncidentStatusSummary: IncidentStatusSummaryFormFields;
}) => (
  <>
    <ReportVersionToggle
      statusSummaryId={statusSummaryId}
      originalReportVersion={originalIncidentStatusSummary?.reportVersion}
    />
    <IncidentTypeCheckboxGroup
      statusSummaryId={statusSummaryId}
      originalIncidentType={originalIncidentStatusSummary?.incidentType}
      originalOtherIncidentType={
        originalIncidentStatusSummary?.otherIncidentType
      }
    />
    <SituationSummaryField
      statusSummaryId={statusSummaryId}
      originalSituationSummary={originalIncidentStatusSummary?.situationSummary}
    />
    <CommandPrioritiesTable
      statusSummaryId={statusSummaryId}
      originalCommandPriorities={
        originalIncidentStatusSummary?.commandPriorities
      }
    />
    <NotableItemsTable
      statusSummaryId={statusSummaryId}
      originalNotableItems={originalIncidentStatusSummary?.notableItems}
    />
    <ResponderInjuriesTable
      statusSummaryId={statusSummaryId}
      originalResponderInjuries={
        originalIncidentStatusSummary?.responderInjuries
      }
    />
    <ResponderDeathsTable
      statusSummaryId={statusSummaryId}
      originalResponderDeaths={originalIncidentStatusSummary?.responderDeaths}
    />
    <LifeSafetyHealthIssuesField
      statusSummaryId={statusSummaryId}
      originalLifeSafetyHealthIssues={
        originalIncidentStatusSummary?.lifeSafetyHealthIssues
      }
    />
    <MarineTransportationSystemField
      statusSummaryId={statusSummaryId}
      originalMarineTransportationSystem={
        originalIncidentStatusSummary?.marineTransportationSystem
      }
    />
    <CriticalResourceRequestsTable
      statusSummaryId={statusSummaryId}
      originalCriticalResourceRequests={
        originalIncidentStatusSummary?.criticalResourceRequests
      }
    />
    <PlannedActionsTable
      statusSummaryId={statusSummaryId}
      originalPlannedActions={originalIncidentStatusSummary?.plannedActions}
    />
    <AssignedResourcesTable
      statusSummaryId={statusSummaryId}
      originalAssignedResources={
        originalIncidentStatusSummary?.assignedResources
      }
    />
    <AmplifyingInformationField
      statusSummaryId={statusSummaryId}
      originalAmplifyingInformation={
        originalIncidentStatusSummary?.amplifyingInformation
      }
    />
    <CommentsField
      statusSummaryId={statusSummaryId}
      originalComments={originalIncidentStatusSummary?.comments}
    />
    <MediaPhotosPublicOutreachField
      statusSummaryId={statusSummaryId}
      originalMediaPhotosPublicOutreach={
        originalIncidentStatusSummary?.mediaPhotosPublicOutreach
      }
    />
  </>
);

export default IncidentStatusSummaryFormComponents;
